/**
 * 線区 (route_id) と方面 (direction_name) のドロップダウンセレクタ。
 *
 * - 上部に常時表示される唯一の入力 UI。
 * - 線区を選ぶと方面の候補が動的に切り替わる。
 * - 「全件 (絞り込まない)」を選択することも可能。
 */
import { useMemo } from 'react';

import type { RouteMaster } from '../data/routes';

interface RouteSelectorProps {
  routes: RouteMaster[];
  /** 現在選択中の route_id (空文字 = 未選択) */
  selectedRouteId: string;
  /** 現在選択中の direction_name (空文字 = 未選択) */
  selectedDirectionName: string;
  onChange: (routeId: string, directionName: string) => void;
  /** 新規作成ボタンが押されたときに呼び出される */
  onCreate?: () => void;
  /** 再読み込みボタン */
  onReload?: () => void;
  /** 一括 Import / Export 等の追加アクション (任意) */
  extraActions?: React.ReactNode;
}

export function RouteSelector({
  routes,
  selectedRouteId,
  selectedDirectionName,
  onChange,
  onCreate,
  onReload,
  extraActions,
}: RouteSelectorProps): React.JSX.Element {
  const selectedRoute = useMemo(
    () => routes.find((r) => r.route_id === selectedRouteId),
    [routes, selectedRouteId],
  );

  const directions = selectedRoute?.directions ?? [];

  return (
    <div className="route-selector">
      <label>
        <span>線区</span>
        <select
          data-testid="route-select"
          aria-label="線区"
          value={selectedRouteId}
          onChange={(e) => {
            const newRouteId = e.target.value;
            // 線区変更時は方面をリセットする (互換性のため空文字で再選択を促す)
            onChange(newRouteId, '');
          }}
        >
          <option value="">(全線区)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_name} ({r.route_id})
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>方面</span>
        <select
          data-testid="direction-select"
          aria-label="方面"
          value={selectedDirectionName}
          onChange={(e) => onChange(selectedRouteId, e.target.value)}
          disabled={!selectedRouteId}
        >
          <option value="">(全方面)</option>
          {directions.map((d) => (
            <option key={d.direction_name} value={d.direction_name}>
              {d.direction_name}
            </option>
          ))}
        </select>
      </label>
      {onCreate ? (
        <button
          type="button"
          data-testid="create-button"
          className="primary create-button"
          onClick={onCreate}
          disabled={!selectedRouteId || !selectedDirectionName}
          title={
            !selectedRouteId || !selectedDirectionName
              ? '新規作成するには線区と方面を選択してください'
              : ''
          }
        >
          ＋ 新規作成
        </button>
      ) : null}
      <div className="actions">
        {extraActions}
        {onReload ? (
          <button type="button" data-testid="reload-button" onClick={onReload}>
            再読み込み
          </button>
        ) : null}
      </div>
    </div>
  );
}
